import { useFormContext, useWatch } from 'react-hook-form-v7';
import type { UseFormReturn } from 'react-hook-form-v7';
import { useMemo } from 'react';
import type { EmployeeFormData } from '@personnel/presentation/types';

/**
 * Custom hook that provides typed access to Employee Form context
 * Eliminates prop-drilling of control, register, errors
 *
 * @returns {Object} Form context with typed methods and state
 *
 * @example
 * const { control, register, errors, watch } = useEmployeeFormContext();
 */
export const useEmployeeFormContext = (methods: UseFormReturn<EmployeeFormData>) => {
  const { control, register, formState, setValue, getValues, watch, clearErrors, setError } = methods;

  return useMemo(
    () => ({
      control,
      register,
      errors: formState.errors,
      setValue,
      getValues,
      watch,
      clearErrors,
      setError,
      methods,
    }),
    [control, register, formState.errors, setValue, getValues, watch, clearErrors, setError, methods],
  );
};

/**
 * Hook for watching specific form fields with proper typing
 *
 * @param {keyof EmployeeFormData} name - Field name to watch
 * @returns {T} Current value of the watched field
 */
export const useEmployeeFormWatch = <K extends keyof EmployeeFormData>(name: K): EmployeeFormData[K] => {
  const { control } = useFormContext<EmployeeFormData>();
  return useWatch({ control, name });
};
